import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, postForm, get, eraCoverUrl, type EraDTO } from "../lib/api";
import { useAuth } from "../lib/auth";
import { capture, type InputMethod } from "../lib/analytics";
import { ConsentGate } from "../components/ConsentGate";
import { InputChooser } from "../components/InputChooser";
import { Header, Spinner, Muted, ErrorText, GildedFrame, QuietButton } from "../components/ui";

// Era → credit → photo → gallery. The server does the work and enforces the
// rules (consent, screen, credit, era release date); this is the surface.

type Step = "era" | "input" | "working";

export function Create() {
  const { credits, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [eras, setEras] = useState<EraDTO[] | null>(null);
  const [pack, setPack] = useState<string | null>(params.get("pack"));
  const [step, setStep] = useState<Step>(params.get("pack") ? "input" : "era");
  const [progress, setProgress] = useState("Preparing…");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        // Same server-gated era list as the app: a scheduled era is invisible
        // on both surfaces at the same moment, with no client release logic.
        setEras((await api.eras()).eras);
      } catch {
        setErr("Couldn't load the eras. Please refresh.");
      }
    })();
  }, []);

  async function onFile(file: File, method: InputMethod) {
    if (!pack) return;
    setStep("working");
    setErr(null);
    try {
      // Spend the credit first: the server refuses to generate without allowance,
      // and a spent credit with no gallery is recoverable while the reverse isn't.
      setProgress("Unlocking this era…");
      await api.redeem(pack);

      setProgress("Checking your photo…");
      const form = new FormData();
      form.append("packId", pack);
      form.append("selfie", file);
      const job = await postForm<{ jobId: string }>("/v1/generate/gallery/async", form);

      capture("webGenerationStarted", { packId: pack, inputMethod: method });
      setProgress("Painting your portraits…");

      // Poll the same async job the app uses.
      for (;;) {
        await new Promise((r) => setTimeout(r, 2500));
        const s = await get<{ status: string; completed?: number; total?: number; error?: string }>(
          `/v1/generate/job?id=${job.jobId}`,
        );
        if (s.completed && s.total) setProgress(`Painting your portraits… ${s.completed} of ${s.total}`);
        if (s.status === "ready") {
          capture("webGenerationCompleted", { packId: pack, inputMethod: method });
          await refresh();
          nav("/gallery");
          return;
        }
        if (s.status === "failed") throw new Error(s.error || "Generation failed.");
      }
    } catch (e) {
      capture("webGenerationFailed", { packId: pack, inputMethod: method });
      setErr(e instanceof Error ? e.message : "Something went wrong.");
      setStep("input");
    }
  }

  if (!eras) return <Spinner />;

  // Deliberately NOT gated on the credit balance. Creating is the thing people
  // came to do, so the eras are always browsable; the credit question is asked at
  // the moment of spending, not at the door.
  //
  // Gating here was also plain wrong: an all-access user sits at available:0
  // forever (they never spend credits), so a balance check locked out exactly the
  // people with unlimited access. `unlimited` now comes down the wire for that.
  const canGenerate = !!credits?.unlimited || (credits?.available ?? 0) >= 1;

  if (step === "working") {
    return (
      <div className="mx-auto max-w-md text-center">
        <GildedFrame>
          <div className="p-8">
            <Spinner />
            <h2 className="mt-2 font-serif text-xl text-ivory">{progress}</h2>
            <Muted className="mt-2">This takes a minute or two. You can leave this page: your gallery is saved to your account.</Muted>
          </div>
        </GildedFrame>
      </div>
    );
  }

  if (step === "input" && pack) {
    return (
      <ConsentGate>
        <Header kicker={eras.find((e) => e.id === pack)?.title || "Create"} title="Add your photo" />
        <ErrorText>{err}</ErrorText>
        <InputChooser packId={pack} onFile={onFile} />
        <div className="mt-6 flex justify-center">
          <QuietButton onClick={() => setStep("era")}>Pick a different era</QuietButton>
        </div>
      </ConsentGate>
    );
  }

  return (
    <>
      <Header kicker="Create" title="Choose an era" />
      <Muted className="mb-4">
        {credits?.unlimited
          ? "You have unlimited access. Pick any era."
          : "One credit unlocks a full gallery of you in that era."}
      </Muted>

      {/* Shown, not enforced: browse first, buy at the moment you actually need to. */}
      {!canGenerate && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gold/25 bg-gold/[0.06] px-4 py-3">
          <Muted className="!text-parchment">
            You're out of credits. Pick an era to see it, or get more to create.
          </Muted>
          <Link to="/buy">
            <QuietButton>Get credits</QuietButton>
          </Link>
        </div>
      )}

      <ErrorText>{err}</ErrorText>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {eras.map((era) => (
          <button
            key={era.id}
            onClick={() => {
              capture("webEraChosen", { packId: era.id, canGenerate });
              // The credit question belongs here, at the point of spending.
              if (!canGenerate) {
                nav(`/buy?from=${encodeURIComponent(era.id)}`);
                return;
              }
              setPack(era.id);
              setStep("input");
            }}
            className="group overflow-hidden rounded-xl border border-gold/25 bg-cardInk text-left transition hover:border-gold/60"
          >
            {eraCoverUrl(era) ? (
              <img
                src={eraCoverUrl(era)!}
                alt={era.title}
                loading="lazy"
                className="aspect-[4/5] w-full bg-screenInk object-cover"
              />
            ) : (
              <div className="aspect-[4/5] w-full bg-screenInk" />
            )}
            <div className="p-3">
              <div className="font-serif text-[15px] text-ivory">{era.title}</div>
              <div className="mt-0.5 text-[11px] text-stoneDim">{era.promptCount} portraits</div>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}
